/**
 * 测试命令
 * 运行各种测试套件
 */

import { Command } from 'commander';
import { runIntegrationTests } from '../tests/integration/integration-test-suite';
import { runPerformanceTests } from '../tests/performance/performance-test-suite';
import { logger } from '../utils/logger';
import chalk from 'chalk';
import * as path from 'path';
import { spawn } from 'child_process';

export function createTestCommand(): Command {
  const command = new Command('test')
    .description('运行测试套件')
    .alias('t');
  
  // 运行所有测试
  command
    .command('all')
    .description('运行所有测试')
    .option('--no-unit', '跳过单元测试')
    .option('--no-integration', '跳过集成测试')
    .option('--no-performance', '跳过性能测试')
    .action(async (options) => {
      console.log(chalk.blue('🧪 运行测试套件...\n'));
      
      const results = {
        unit: { status: 'skipped', passed: 0, failed: 0 },
        integration: { status: 'skipped', passed: 0, failed: 0 },
        performance: { status: 'skipped', passed: 0, failed: 0 }
      };
      
      // 单元测试
      if (options.unit !== false) {
        console.log(chalk.bold('\n📦 单元测试:\n'));
        try {
          await runUnitTests();
          results.unit.status = 'passed';
        } catch (error) {
          results.unit.status = 'failed';
          console.error(chalk.red('单元测试失败'), error);
        }
      }
      
      // 集成测试
      if (options.integration !== false) {
        console.log(chalk.bold('\n🔗 集成测试:\n'));
        try {
          await runIntegrationTests();
          results.integration.status = 'passed';
        } catch (error) {
          results.integration.status = 'failed';
          console.error(chalk.red('集成测试失败'), error);
        }
      }
      
      // 性能测试
      if (options.performance !== false) {
        console.log(chalk.bold('\n⚡ 性能测试:\n'));
        try {
          await runPerformanceTests();
          results.performance.status = 'passed';
        } catch (error) {
          results.performance.status = 'failed';
          console.error(chalk.red('性能测试失败'), error);
        }
      }
      
      // 打印总结
      printTestSummary(results);
    });
  
  // 单元测试
  command
    .command('unit')
    .description('运行单元测试')
    .option('-p, --pattern <pattern>', '测试文件模式', '**/*.test.ts')
    .option('-w, --watch', '监视模式')
    .option('--coverage', '生成覆盖率报告')
    .action(async (options) => {
      console.log(chalk.blue('📦 运行单元测试...\n'));
      
      try {
        await runUnitTests(options);
        console.log(chalk.green('\n✅ 单元测试完成'));
      } catch (error) {
        console.error(chalk.red('\n❌ 单元测试失败'), error);
        process.exit(1);
      }
    });
  
  // 集成测试
  command
    .command('integration')
    .alias('int')
    .description('运行集成测试')
    .option('-s, --scenario <name>', '运行特定场景')
    .option('-t, --timeout <ms>', '测试超时时间', '30000')
    .action(async (options) => {
      console.log(chalk.blue('🔗 运行集成测试...\n'));
      
      try {
        await runIntegrationTests();
        console.log(chalk.green('\n✅ 集成测试完成'));
      } catch (error) {
        console.error(chalk.red('\n❌ 集成测试失败'), error);
        process.exit(1);
      }
    });
  
  // 性能测试
  command
    .command('performance')
    .alias('perf')
    .description('运行性能测试')
    .option('-s, --scenario <name>', '运行特定场景')
    .option('--no-warmup', '跳过预热')
    .action(async (options) => {
      console.log(chalk.blue('⚡ 运行性能测试...\n'));
      
      try {
        await runPerformanceTests();
        console.log(chalk.green('\n✅ 性能测试完成'));
      } catch (error) {
        console.error(chalk.red('\n❌ 性能测试失败'), error);
        process.exit(1);
      }
    });
  
  // E2E 测试
  command
    .command('e2e')
    .description('运行端到端测试')
    .option('--headless', '无头模式运行')
    .option('--browser <browser>', '浏览器选择', 'chromium')
    .action(async (options) => {
      console.log(chalk.blue('🌐 运行 E2E 测试...\n'));
      
      try {
        await runE2ETests(options);
        console.log(chalk.green('\n✅ E2E 测试完成'));
      } catch (error) {
        console.error(chalk.red('\n❌ E2E 测试失败'), error);
        process.exit(1);
      }
    });
  
  // 测试覆盖率
  command
    .command('coverage')
    .description('生成测试覆盖率报告')
    .option('-o, --output <path>', '报告输出路径', './coverage')
    .action(async (options) => {
      console.log(chalk.blue('📊 生成测试覆盖率报告...\n'));
      
      try {
        await generateCoverageReport(options);
        console.log(chalk.green('\n✅ 覆盖率报告已生成'));
        console.log(chalk.dim(`报告位置: ${options.output}`));
      } catch (error) {
        console.error(chalk.red('生成覆盖率报告失败'), error);
      }
    });
  
  // 验证测试
  command
    .command('validate')
    .description('验证测试配置和环境')
    .action(async () => {
      console.log(chalk.blue('🔍 验证测试环境...\n'));
      
      const checks = [
        { name: 'Node.js 版本', check: checkNodeVersion },
        { name: '依赖安装', check: checkDependencies },
        { name: '测试文件', check: checkTestFiles },
        { name: '配置文件', check: checkConfig }
      ];
      
      let allPassed = true;
      
      for (const { name, check } of checks) {
        process.stdout.write(`检查 ${name}... `);
        const result = await check();
        if (result.passed) {
          console.log(chalk.green('✓'));
        } else {
          console.log(chalk.red('✗'));
          console.log(chalk.dim(`  ${result.message}`));
          allPassed = false;
        }
      }
      
      if (allPassed) {
        console.log(chalk.green('\n✅ 所有检查通过'));
      } else {
        console.log(chalk.red('\n❌ 部分检查失败'));
        process.exit(1);
      }
    });
  
  return command;
}

/**
 * 运行单元测试
 */
async function runUnitTests(options?: any): Promise<void> {
  return new Promise((resolve, reject) => {
    const args = ['node_modules/.bin/jest'];
    
    if (options?.pattern) {
      args.push('--testPathPattern', options.pattern);
    }
    
    if (options?.watch) {
      args.push('--watch');
    }
    
    if (options?.coverage) {
      args.push('--coverage');
    }
    
    const proc = spawn('npx', args, {
      stdio: 'inherit',
      shell: true
    });
    
    proc.on('close', (code) => {
      if (code === 0) {
        resolve();
      } else {
        reject(new Error(`Jest exited with code ${code}`));
      }
    });
  });
}

/**
 * 运行 E2E 测试
 */
async function runE2ETests(options: any): Promise<void> {
  return new Promise((resolve, reject) => {
    const args = ['playwright', 'test'];
    
    if (options.headless) {
      args.push('--headed');
    }
    
    if (options.browser) {
      args.push('--browser', options.browser);
    }
    
    const proc = spawn('npx', args, {
      stdio: 'inherit',
      shell: true
    });
    
    proc.on('close', (code) => {
      if (code === 0) {
        resolve();
      } else {
        reject(new Error(`Playwright exited with code ${code}`));
      }
    });
  });
}

/**
 * 生成覆盖率报告
 */
async function generateCoverageReport(options: any): Promise<void> {
  return new Promise((resolve, reject) => {
    const args = [
      'jest',
      '--coverage',
      '--coverageDirectory',
      options.output,
      '--coverageReporters',
      'html',
      'text',
      'lcov'
    ];
    
    const proc = spawn('npx', args, {
      stdio: 'inherit',
      shell: true
    });
    
    proc.on('close', (code) => {
      if (code === 0) {
        resolve();
      } else {
        reject(new Error(`Coverage generation failed with code ${code}`));
      }
    });
  });
}

/**
 * 打印测试总结
 */
function printTestSummary(results: any): void {
  console.log('\n' + '='.repeat(60));
  console.log(chalk.bold('测试总结'));
  console.log('='.repeat(60) + '\n');
  
  const suites = [
    { name: '单元测试', result: results.unit },
    { name: '集成测试', result: results.integration },
    { name: '性能测试', result: results.performance }
  ];
  
  suites.forEach(({ name, result }) => {
    const icon = result.status === 'passed' ? '✅' :
                 result.status === 'failed' ? '❌' : '⏭️';
    const color = result.status === 'passed' ? chalk.green :
                  result.status === 'failed' ? chalk.red : chalk.gray;
    
    console.log(`${icon} ${color(name)}: ${result.status}`);
  });
  
  console.log('\n' + '='.repeat(60));
  
  const hasFailures = Object.values(results).some((r: any) => r.status === 'failed');
  if (hasFailures) {
    console.log(chalk.red('\n❌ 测试失败'));
    process.exit(1);
  } else {
    console.log(chalk.green('\n✅ 所有测试通过'));
  }
}

/**
 * 环境检查函数
 */
async function checkNodeVersion(): Promise<{ passed: boolean; message?: string }> {
  const version = process.version;
  const major = parseInt(version.split('.')[0].substring(1));
  
  if (major >= 16) {
    return { passed: true };
  } else {
    return { 
      passed: false, 
      message: `需要 Node.js 16+，当前版本: ${version}` 
    };
  }
}

async function checkDependencies(): Promise<{ passed: boolean; message?: string }> {
  try {
    require('jest');
    require('@playwright/test');
    return { passed: true };
  } catch (error) {
    return { 
      passed: false, 
      message: '缺少测试依赖，请运行 npm install' 
    };
  }
}

async function checkTestFiles(): Promise<{ passed: boolean; message?: string }> {
  const fs = require('fs-extra');
  const testFiles = await fs.readdir(path.join(__dirname, '../tests'));
  
  if (testFiles.length > 0) {
    return { passed: true };
  } else {
    return { 
      passed: false, 
      message: '未找到测试文件' 
    };
  }
}

async function checkConfig(): Promise<{ passed: boolean; message?: string }> {
  const fs = require('fs-extra');
  const configFiles = ['jest.config.js', 'playwright.config.ts'];
  
  for (const file of configFiles) {
    if (!await fs.pathExists(file)) {
      return { 
        passed: false, 
        message: `缺少配置文件: ${file}` 
      };
    }
  }
  
  return { passed: true };
}